import { formatRegionUrl } from "@/utils/string-utils";
import Vue from "vue";
import { MutationTree } from "vuex";
import { Region } from "../types";
import { getDefaultState } from "./state";
import { RegionalLogin, RegionState } from "./types";

export const mutations: MutationTree<RegionState> = {
	/**
	 * Sets the regions the user has access too.
	 * @param state The vuex state.
	 * @param regions The list of regions.
	 */
	setRegions(state, regions: Region[]) {
		// Create a lookup object, mapping the regionId to the region data,
		// and update the store.
		state.regions = regions.reduce((prev, curr) => {
			// Format the region URL
			curr.url = formatRegionUrl(curr.url);

			// Put it in our lookup
			prev[curr.regionId] = curr;
			return prev;
		}, {});
	},

	/**
	 * Sets an XSRF token for a specified region url.
	 * @param state The vuex state.
	 * @param param1 The url of the region, and the token.
	 */
	setXsrfToken(state, { regionUrl, xsrfToken }) {
		Vue.set(state.xsrfTokens, regionUrl, xsrfToken);
	},

	/**
	 * Clears an XSRF token for a specified region url.
	 * @param state The vuex state.
	 * @param regionUrl The url of the region.
	 */
	clearXsrfToken(state, regionUrl) {
		if (typeof state.xsrfTokens[regionUrl] != "undefined") {
			Vue.delete(state.xsrfTokens, regionUrl);
		}
	},

	/**
	 * Sets the user login result for a specified region id.
	 * @param state The vuex state.
	 * @param param1 The id of the region, the login result, and the heartbeat interval.
	 */
	setLogin(state, { regionId, loginResult, heartbeat }) {
		const newLogin: RegionalLogin = {
			userLoginResult: loginResult,
			heartbeat,
			referenceCount: 1
		};

		Vue.set(state.logins, regionId, newLogin);
	},

	/**
	 * Clears a regional user login result for a specified region id.
	 * @param state The vuex state.
	 * @param regionId The id of the region.
	 */
	clearLogin(state, regionId) {
		Vue.delete(state.logins, regionId);
	},

	/**
	 * Clears all current regional logins.
	 * @param state The vuex state.
	 */
	clearLogins(state) {
		state.logins = {};
	},

	/**
	 * Increments the reference count for the user session in a specified region.
	 * @param state The vuex state.
	 * @param regionId The id of the region.
	 */
	incrementLoginReferenceCount(state, regionId: number) {
		if (state.logins[regionId]) {
			state.logins[regionId].referenceCount++;
		}
	},

	/**
	 * Decrements the reference count for a user session in a specified region.
	 * @param state The vuex state.
	 * @param regionId The id of the region.
	 */
	decrementLoginReferenceCount(state, regionId: number) {
		if (state.logins[regionId]) {
			state.logins[regionId].referenceCount--;
		}
	},

	/**
	 * Resets the state of the regions store module.
	 * @param state The vuex state.
	 */
	resetState(state) {
		Object.assign(state, getDefaultState());
	},

	/**
	 * Sets the state of the allRegions store.
	 * @param state The vuex state.
	 * @param regions List of all regions in the database.
	 */
	setAllRegions: (state, regions: Region[]) => (state.allRegions = regions),

	/**
	 * Add or update region from the allRegions store.
	 * @param state The vuex state.
	 * @param data The region to be added or updated to the state.
	 */
	setRegion(state, data: Region) {
		const exists: number = state.allRegions.findIndex(x => x.regionId == data.regionId);
		if (exists >= 0) {
			Vue.set(state.allRegions, exists, data);
		} else {
			state.allRegions.push(data);
		}
	},

	/**
	 * Remove region from the allRegions store.
	 * @param state The vuex state.
	 * @param data The region id removed from the state.
	 */
	removeRegion(state, data: number) {
		const index: number = state.allRegions.findIndex(x => x.regionId == data);
		if (index >= 0) {
			state.allRegions.splice(index, 1);
		}
	}
};
